import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadAllBriefs } from 'redux/modules/clientATS';
import { loadTalentPool } from 'redux/modules/portfolio';
import { inviteUser } from 'redux/modules/briefDetails';
import { setSuccessMessage } from 'redux/modules/successMessage';
import { setErrorMessage } from 'redux/modules/errorMessage';
import { INVITE_SENT_SUCCESS } from 'constants/successMessages';
import { Grid } from 'react-bootstrap';
import Button from 'react-bootstrap/lib/Button';
import { trackEvent, reloadAuthAndTrackHireSuccess } from 'helpers/Analytics';
import { loadPartners } from 'redux/modules/partners';
import NextStepsModal from 'components/ClientATS/NextStepsModal/NextStepsModal';
import { getProfileCompletion } from 'helpers/profileCompletion';

import DashboardHero from 'components/ClientATS/Dashboard/DashboardHero';
import JobCardsCarousel from 'components/ClientATS/Dashboard/JobCardsCarousel';
import TalentPoolCarousel from 'components/ClientATS/Dashboard/TalentPoolCarousel';
import ManageBriefsBanner from 'containers/Ads/ManageBriefsBanner/ManageBriefsBanner';

import styles from './Dashboard.module.scss';

const getDescriptionFromSpec = (spec) => {
  if (!spec) return '';
  const lines = spec.split('\n');
  const descriptionLines = [];

  for (const line of lines) {
    if (line.trim().startsWith('**')) break;
    if (line.trim()) descriptionLines.push(line.trim());
  }

  return descriptionLines.join(' ').replace(/^['"]+|['"]+$/g, '');
};

const TALENT_POOL_LIMIT = 100;

const Dashboard = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { briefs, loadingAllBriefs, loadedAllBriefs } = useSelector(
    (state) => state.clientATS
  );
  const portfolio = useSelector((state) => state.portfolio || {});
  const { partners, loadedPartners } = useSelector((state) => state.partners);
  const ownBriefs = useSelector((state) => state.auth?.ownBriefs) || {};
  const briefEntities = useSelector((state) => state.entities?.briefs) || {};

  const [showNextStepsModal, setShowNextStepsModal] = useState(false);

  const { isProfileComplete, profileCompletionPercentage } =
    getProfileCompletion(auth.user);

  useEffect(() => {
    if (auth.user && auth.user.id) {
      if (!loadedAllBriefs) {
        dispatch(loadAllBriefs(auth.user.id));
      }
    }
  }, [auth.user, loadedAllBriefs, dispatch]);

  useEffect(() => {
    if (!loadedAllBriefs) return;

    const search = window.location.search || '';
    if (
      !search.includes('payment-successful') &&
      !search.includes('payment-cancelled')
    ) {
      return;
    }

    const params = new URLSearchParams(search);
    const hasPaymentSuccess = params.has('payment-successful');
    const hasPaymentCancelled = params.has('payment-cancelled');

    if (hasPaymentSuccess) {
      dispatch(
        setSuccessMessage('Your payment has been processed successfully')
      );
      if (params.get('hire') === '1') {
        dispatch(reloadAuthAndTrackHireSuccess());
      }
    } else if (hasPaymentCancelled) {
      dispatch(setErrorMessage('Your payment has been cancelled'));
    }

    window.history.replaceState(null, null, window.location.pathname);
  }, [dispatch, loadedAllBriefs]);

  useEffect(() => {
    if (!loadedPartners) {
      dispatch(loadPartners('manage_jobs'));
    }
  }, [dispatch, loadedPartners]);

  const username = auth.user?.username;
  const userPortfolio = username ? portfolio[username] || {} : {};

  useEffect(() => {
    if (!username) return;
    if (!userPortfolio.loadedShortlists && !userPortfolio.loadingShortlists) {
      dispatch(loadTalentPool(username, 1, TALENT_POOL_LIMIT));
    }
  }, [
    dispatch,
    username,
    userPortfolio.loadedShortlists,
    userPortfolio.loadingShortlists,
  ]);

  const activeBriefs = useMemo(() => {
    if (!briefs) return [];
    return briefs
      .filter((brief) => {
        const activeHiresCount = brief.stats?.active_hires || 0;
        return (
          brief.status === 'active' ||
          brief.status === 'pending_approval' ||
          activeHiresCount > 0
        );
      })
      .map((brief) => ({
        ...brief,
        description: getDescriptionFromSpec(brief.spec),
      }));
  }, [briefs]);

  const pastBriefs = useMemo(() => {
    if (!briefs) return [];
    return briefs
      .filter((brief) => {
        const activeHiresCount = brief.stats?.active_hires || 0;
        return (
          brief.status !== 'active' &&
          brief.status !== 'pending_approval' &&
          activeHiresCount === 0
        );
      })
      .map((brief) => ({
        ...brief,
        description: getDescriptionFromSpec(brief.spec),
      }));
  }, [briefs]);

  const pastCount = pastBriefs.length;

  const openJobsCount = useMemo(() => {
    if (!briefs) return 0;
    return briefs.filter(
      (brief) => brief.status === 'active' && brief?.stats?.active_hires === 0
    ).length;
  }, [briefs]);

  const activeHireJobsCount = useMemo(() => {
    if (!briefs) return 0;
    return briefs.filter((brief) => (brief.stats?.active_hires || 0) > 0)
      .length;
  }, [briefs]);

  const talentPoolUsers = useMemo(() => {
    if (!username) return [];
    const shortlist = userPortfolio.shortlist || [];
    return shortlist.map((shortlistItem) => shortlistItem.user).filter(Boolean);
  }, [username, userPortfolio.shortlist]);

  const activeBriefsForInvite = useMemo(() => {
    const ids = ownBriefs.collaborations;
    if (!Array.isArray(ids)) return [];
    return ids
      .map((id) => briefEntities[id])
      .filter((brief) => brief && typeof brief === 'object');
  }, [ownBriefs.collaborations, briefEntities]);

  const loadingTalentPool = userPortfolio.loadingShortlists || false;
  const talentPoolCount = talentPoolUsers.length;

  const handleInvite = (brief, user) => {
    dispatch(inviteUser(auth.user, user.id, brief))
      .then(() => {
        dispatch(setSuccessMessage(INVITE_SENT_SUCCESS));
      })
      .catch(() => {});
    trackEvent('user-invites-freelancer', { type: 'dashboard_talent_pool' });
  };

  return (
    <Grid className={styles.wrapper}>
      <DashboardHero
        user={auth.user}
        showNextStepsButton={!!auth.user && !isProfileComplete}
        profileCompletionPercentage={profileCompletionPercentage}
        onNextStepsClick={() => {
          trackEvent('client-ats-dashboard-opens-next-steps');
          setShowNextStepsModal(true);
        }}
      />
      <div className={styles.content}>
        <header className={styles.pageHeader}>
          <div className={styles.pageHeaderMain}>
            <h2 className={styles.pageTitle}>Job Dashboard</h2>
            <p className={styles.pageDescription}>
              Manage your active projects, review past projects, and explore
              your talent pool
            </p>
          </div>
          <Button
            bsStyle="primary"
            bsSize="lg"
            id="hire-a-creative-header-button-desktop"
            className={styles.postJobBtn}
            href="/post-a-job"
          >
            Post a Project
          </Button>
        </header>
        <section className={styles.activeJobsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Active</h2>
            <p className={styles.sectionSubtitle}>
              {loadingAllBriefs
                ? '\u00A0'
                : `${openJobsCount} ${
                    openJobsCount === 1 ? 'open position' : 'open positions'
                  } \u00b7 ${activeHireJobsCount} ${
                    activeHireJobsCount === 1
                      ? 'job with active hire'
                      : 'jobs with active hires'
                  }`}
            </p>
          </div>
          <JobCardsCarousel
            briefs={activeBriefs}
            loadingAllBriefs={loadingAllBriefs}
            active
          />
        </section>
        <section className={styles.bannerSection}>
          <div className={styles.bannerWrapper}>
            <ManageBriefsBanner
              userLocationText={auth.user?.location?.text}
              partners={partners}
              loadedPartners={loadedPartners}
            />
          </div>
        </section>
        <section className={styles.talentPoolSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Talent Pool</h2>
            <p className={styles.sectionSubtitle}>
              {loadingTalentPool
                ? '\u00A0'
                : `${talentPoolCount} ${
                    talentPoolCount === 1 ? 'expert' : 'experts'
                  } in your pool`}
            </p>
          </div>
          <TalentPoolCarousel
            users={talentPoolUsers}
            onInvite={handleInvite}
            collaborations={activeBriefsForInvite}
            loadingTalentPool={loadingTalentPool}
          />
        </section>
        <section className={styles.pastJobsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Past</h2>
            <p className={styles.sectionSubtitle}>
              {loadingAllBriefs
                ? '\u00A0'
                : `${pastCount} ${
                    pastCount === 1 ? 'closed position' : 'closed positions'
                  }`}
            </p>
          </div>
          <JobCardsCarousel
            briefs={pastBriefs}
            loadingAllBriefs={loadingAllBriefs}
          />
        </section>
      </div>
      <NextStepsModal
        show={showNextStepsModal}
        onHide={() => setShowNextStepsModal(false)}
      />
    </Grid>
  );
};

export default Dashboard;
